'use client';

import { useQueryClient } from '@tanstack/react-query';

import { Avatar } from '@/components/atoms/Avatar';
import { Button } from '@/components/atoms/Button';
import { PlayerType } from '@/components/constants/enums';
import { MatchSide, SelectedAwayPlayers, SelectedHomePlayers } from '@/components/constants/types';
import { UserDetails } from '@/components/molecules/UserDetails';
import { useScoreCard } from '@/contexts/ScoreCardProvider';
import { TeamLeaguePlayerInfo } from '@/entities';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useChangePlayerMutation } from '@/hooks/matches/useChangePlayer';

interface PlayerCardProps {
	teamPlayerInfo?: TeamLeaguePlayerInfo;
	matchId: string;
	team: MatchSide;
	playerPosition: PlayerType;
	onCloseModal: () => void;
	setSelectedHomePlayers: (players: SelectedHomePlayers) => void;
	setSelectedAwayPlayers: (players: SelectedAwayPlayers) => void;
	selectedHomePlayers: SelectedHomePlayers;
	selectedAwayPlayers: SelectedAwayPlayers;
}

export function PlayerCard({
	teamPlayerInfo,
	matchId,
	team,
	playerPosition,
	onCloseModal,
	setSelectedHomePlayers,
	setSelectedAwayPlayers,
	selectedHomePlayers,
	selectedAwayPlayers
}: PlayerCardProps) {
	const queryClient = useQueryClient();
	const { matchupData } = useScoreCard();
	const { mutate: changePlayerMutation } = useChangePlayerMutation({
		onSuccessCallback: () => {
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupData.uuid) });
		}
	});

	const getUpdateField = () => {
		if (team === 'home') {
			const updateSwappedOutField = playerPosition === 'playerOne' ? 'swappedOutButPlayedTeam1UserId1' : 'swappedOutButPlayedTeam1UserId2';
			const updateSelectedPlayerField = playerPosition === 'playerOne' ? 'teamOnePlayerOneUuid' : 'teamOnePlayerTwoUuid';
			setSelectedHomePlayers({
				...selectedHomePlayers,
				[updateSelectedPlayerField]: teamPlayerInfo?.userUuid
			});
			return updateSwappedOutField;
		} else {
			const updateSwappedOutField = playerPosition === 'playerOne' ? 'swappedOutButPlayedTeam2UserId1' : 'swappedOutButPlayedTeam2UserId2';
			const updateSelectedPlayerField = playerPosition === 'playerOne' ? 'teamTwoPlayerOneUuid' : 'teamTwoPlayerTwoUuid';
			setSelectedAwayPlayers({
				...selectedAwayPlayers,
				[updateSelectedPlayerField]: teamPlayerInfo?.userUuid
			});
			return updateSwappedOutField;
		}
	};

	const handleSelectPlayer = () => {
		const updateField = getUpdateField();
		changePlayerMutation({
			matchId: matchId,
			updateData: {
				[updateField]: teamPlayerInfo?.userUuid,
				lineupChangeIsSubstitution: true
			}
		});
		onCloseModal();
	};

	return (
		<div className=" max-w-md rounded-lg border border-purple-200 bg-purple-25 shadow-lg">
			<div className="flex flex-wrap items-center justify-center gap-4 p-4 sm:items-start sm:justify-start sm:flex-nowrap">
				<div className="flex w-full sm:w-[62px] flex-col items-center text-center">
					<Avatar src={teamPlayerInfo?.profilePictureUrl} alt={`${teamPlayerInfo?.firstName}'s profile picture`} width={60} height={60} />
					<p className="break-words text-xs leading-[18px] text-gray-900">{teamPlayerInfo?.state.title}</p>
					<p className="break-words text-xs uppercase text-gray-500">{teamPlayerInfo?.country.abbreviation}</p>
				</div>

				<UserDetails
					name={teamPlayerInfo?.fullName ?? ''}
					age={teamPlayerInfo?.dob ?? ''}
					gender={teamPlayerInfo?.gender ?? ''}
					email={teamPlayerInfo?.email ?? ''}
					phone={teamPlayerInfo?.phone ?? ''}
					ratings={{
						double: teamPlayerInfo?.formattedRatings.doubles ?? 'NR',
						single: teamPlayerInfo?.formattedRatings.singles ?? 'NR'
					}}
				/>

				<Button
					onClick={handleSelectPlayer}
					className="h-[26px] w-full sm:w-[54px] self-end !rounded-[4px] bg-orange-500 px-2 py-1 text-xs font-semibold leading-[18px] text-white hover:bg-orange-700"
				>
					Select
				</Button>
			</div>
		</div>
	);
}
